/*
 * Page for setting up and maintaining integration settings for Xero
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Paper } from '@mui/material';
import { FormControl, Input, InputLabel, NativeSelect, Typography, TextField  } from '@mui/material';

import BackButton from '../ui/BackButton';
import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import AppBindStyles from '../../AppBindStyles';
import AppLogo from '../ui/AppLogo';

const abEnvironment = new AbEnvironment();
const IntegrationSetupXeroQuery = graphql`
query IntegrationSetupXeroQuery($accountDelegatedAuthsId: UUID!) {
  accountDelegatedAuthsTByAccountDelegatedAuthsId(accountDelegatedAuthsId: $accountDelegatedAuthsId) {
    appName
    authType
    appData
    appIntegrationTByAppIntegrationId {
      appDescription
      appLogo
    }
    accountDelegatedAuthsMapTsByAccountDelegatedAuthsId {
      totalCount
      nodes {
        accountDelegatedAuthsMapId
        mapName
        entityId
        entityName
        entityDescription
      }
    }
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      //marginTop: 10,
    },
    flex: {
      flex: 1,
    },
    contactSelect: {
      width: 420,
    },
    contactNameField: {
      width: 360,
    },
    accountSelect: {
      width: 370,
    },
    codeField: {
      width: 100,
    },
    descField: {
      minWidth: 486,
    },
    paper: {
        [theme.breakpoints.up('xs')]: {
          padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
          padding: theme.spacing(2),
        },
        [theme.breakpoints.up('md')]: {
          padding: theme.spacing(3),
        },
        textAlign: 'left',
        color: theme.palette.text.secondary,
        maxWidth: 1200,
        marginTop: 16,
    }
});

class IntegrationSetupXero extends Component {
  state = {
    contact_list: [],
    acct_list: [],
    acct_list_cur: [],
    acct_list_dir: [],
    acct_list_exp: [],
    acct_list_bank: [],
    contact_id: null,
    contact_name: '',
    asset_id: null,
    asset_code: '',
    asset_name: '',
    cogs_id: null,
    cogs_code: '',
    cogs_name: '',
    abfee_id: null,
    abfee_code: '',
    abfee_name: '',
    ppfee_id: null,
    ppfee_code: '',
    ppfee_name: '',
    bank_id: null,
    lookup_error: null
  };

  componentDidMount() {
    // Load hook
    const { isAuthenticatedReseller, needAssent, callPartnerApi, logError } = this.props.auth;
    if (isAuthenticatedReseller() &&  ! needAssent()) { 
  
      let payload = {
        operation: "XeroGetAccounts"
      };
  
      // Make value available
      let c_list      = [];
      let a_list      = []; // All accounts
      let a_list_cur  = []; // CURRENT (current asset)
      let a_list_dir  = []; // DIRECTCOSTS (e.g. cogs)
      let a_list_exp  = []; // EXPENSE
      let a_list_bank = []; // BANK

      callPartnerApi('appbind/generic',
                     'POST',
                     payload
      )
      .then(resp => {
        return resp.json();
      })
      .then(response => {
        if (response != null && response.status < 0) {
          throw "Unable to retrieve account";
        }

        if (response != null && response.body != null && response.body.body != null && response.body.body.Accounts != null) {
          a_list = response.body.body.Accounts;

          for (let i=0; i<a_list.length; i++) {
            let theType = a_list[i].Type;
            if (theType === 'CURRENT') {
              a_list_cur.push(a_list[i]);
            } else if (theType === 'DIRECTCOSTS') {
              a_list_dir.push(a_list[i]);
            } else if (theType === 'EXPENSE') {
              a_list_exp.push(a_list[i]);
            } else if (theType === 'BANK') {
              a_list_bank.push(a_list[i]);
            }
          }    
        }

        // Now lookup AppBind contact
        payload = {
          operation: "XeroGetContact",
          params: {
            contactName: 'AppBind'
          }
        }
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);

      })
      .then(resp => {
        return resp.json();
      })
      .then(response => {
        if (response != null && response.status < 0) {
          throw "Unable to retrieve contact";
        }
        if (response != null && response.body != null && response.body.body != null && response.body.body.Contacts != null) {
          c_list = response.body.body.Contacts;
        }

        this.setState({contact_list: c_list,
                       acct_list: a_list,
                       acct_list_cur: a_list_cur,
                       acct_list_dir: a_list_dir,
                       acct_list_exp: a_list_exp,
                       acct_list_bank: a_list_bank});
      })
      .catch(error => {
        logError('Exception in IntegrationSetupXero.js: ' + error);
        this.setState({lookup_error: JSON.stringify(error)});
      });
    }
  }

  disconnectFromApp(adaId) {
    const { callPartnerApi } = this.props.auth;

    if (! adaId) {
      return;
    }

    let payload = {
          operation: "OAuth2Revoke",
          params: {
            accountDelegatedAuthsId:  adaId
          }
        };

    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      if (data.link) {
        window.location.replace(data.link);
      } else {
        window.location.reload();
      }
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  doesNameAlreadyExist(aName) {
    if (aName === '' || this.state.contact_list == null || this.state.contact_list.length === 0) {
      return false;
    }

    let found_match = false;
    for (let i=0; i<this.state.contact_list.length; i++) {
      let n = this.state.contact_list[i];
      if (n.Name != null && n.Name === aName) {
        found_match = true;
        break;
      }
    }

    return found_match;
  }

  doesCodeAlreadyExist(aCode) {
    if (aCode === '' || this.state.acct_list == null || this.state.acct_list.length === 0) {
      return false;
    }

    let found_match = false;
    for (let i=0; i<this.state.acct_list.length; i++) {
      let n = this.state.acct_list[i];
      if (n.Code != null && n.Code === aCode) {
        found_match = true;
        break;
      }
    }

    return found_match;
  }

  /*
   * Should save be disabled?  We only want it enabled if there are changes to save, and
   * all required values are set.  The parameters coming in will be blank if a new contact
   * or account is to be created, and  non-blank if an existing id is being used.
   * So if blank, require that additional fields are set.
   */
  isSaveDisabled(contactId, assetId, cogsId, abfeeId, ppfeeId) {
    if (contactId === '') {
      if (this.state.contact_name == null || this.state.contact_name === '') return true;
      if (this.doesNameAlreadyExist(this.state.contact_name)) return true;
    }

    /*
    if (assetId === '') {
      if (this.state.asset_code == null || this.state.asset_code === '') return true;
      if (this.state.asset_name == null || this.state.asset_name === '') return true;
      if (this.doesCodeAlreadyExist(this.state.asset_code)) return true;
    }
    */

    if (cogsId === '') {
      if (this.state.cogs_code == null || this.state.cogs_code === '') return true;
      if (this.state.cogs_name == null || this.state.cogs_name === '') return true;
      if (this.doesCodeAlreadyExist(this.state.cogs_code))  return true;
    }

    if (abfeeId === '') {
      if (this.state.abfee_code == null || this.state.abfee_code === '') return true;
      if (this.state.abfee_name == null || this.state.abfee_name === '') return true;
      if (this.doesCodeAlreadyExist(this.state.abfee_code)) return true;
    }

    /*
    if (ppfeeId === '') {
      if (this.state.ppfee_code == null || this.state.ppfee_code === '') return true;
      if (this.state.ppfee_name == null || this.state.ppfee_name === '') return true;
      if (this.doesCodeAlreadyExist(this.state.ppfee_code)) return true;
    }
    */

    return false;
  }

  selectContactEntry(contactId) {
    if (contactId === '') {
      // Creating new
      this.setState({contact_id: ''});
    } else {
      // Selecting existing, so clear out name
      this.setState({contact_id: contactId, contact_name: ''});
    }
  }

  selectAssetEntry(assetId) {
    if (assetId === '') {
      this.setState({asset_id: '', asset_code: '', asset_name: ''});
    } else {
      this.setState({asset_id: assetId, asset_code: '', asset_name: ''});
    }
  }

  selectCogsEntry(cogsId) {
    if (cogsId === '') {
      this.setState({cogs_id: '', cogs_code: '', cogs_name: ''});
    } else {
      this.setState({cogs_id: cogsId, cogs_code: '', cogs_name: ''});
    }
  }

  selectAbfeeEntry(abfeeId) {
    if (abfeeId === '') {
      this.setState({abfee_id: '', abfee_code: '', abfee_name: ''});
    } else {
      this.setState({abfee_id: abfeeId, abfee_code: '', abfee_name: ''});
    }
  }

  selectPpfeeEntry(ppfeeId) {
    if (ppfeeId === '') {
      this.setState({ppfee_id: '', ppfee_code: '', ppfee_name: ''});
    } else {
      this.setState({ppfee_id: ppfeeId, ppfee_code: '', ppfee_name: ''});
    }
  }

  selectBankEntry(bankId) {
    if (bankId === '') {
      this.setState({bank_id: ''});
    } else {
      this.setState({bank_id: bankId});
    }
  }

  getNameForContact(contactId) {
    let contactName = '';
    if (this.state.contact_list) {
      for (let i=0; i<this.state.contact_list.length; i++) {
        let n = this.state.contact_list[i];
        if (n.ContactID === contactId) {
          contactName = n.Name;
          break;
        }
      }
    }

    return contactName;
  }

  getCodeAndNameForAccount(accountId) {
    let codeAndName = {
      Code: '',
      Name: ''
    };

    if (this.state.acct_list != null) {
      for (let i=0; i<this.state.acct_list.length; i++) {
        let n = this.state.acct_list[i];
        if (n.AccountID === accountId) {
          codeAndName.Code = n.Code;
          codeAndName.Name = n.Name;
          break;
        }
      }
    }

    return codeAndName;
  }

  /* Call API to save settings, as this may involve api calls to push entities to the integrated app */
  saveSettings(adaId, appBindContact, appBindAssetAccount, saasCogsAccount, abFeeAccount, ppFeeAccount, bankAccount) {
    const { callPartnerApi } = this.props.auth;

    // If parms are non-null, we have mappings.
    let payload = {
      operation: "XeroUpdateMappings",
      params: {
        accountDelegatedAuthsId:  adaId,
        AppBindContact: {
          map_id: '',
          id: '',
          name: '',
        },
        /*
        AppBindAssetAccount: {
          map_id: '',
          id: '',
          code: '',
          name: '',
        },
        */ 
        AppBindAssetAccount: null,
        SaaSCOGSAccount: {
          map_id: '',
          id: '',
          code: '',
          name: '',
        },
        AppBindFeeAccount: {
          map_id: '',
          id: '',
          code: '',
          name: '',
        },
        /*
        AppBindPPAccount: {
          map_id: '',
          id: '',
          code: '',
          name: '',
        },
        */
        AppBindPPAccount: null,
        /*
        BankAccount: {
          map_id: '',
          id: '',
          name: '',
        }
        */
        BankAccount: null
      }
    };

    // Contact
    if (appBindContact == null) {
      // Existing or new contact on new map.
      if (this.state.contact_id != null && this.state.contact_id != '') {
        // Mapping to an existing contact
        payload.params.AppBindContact.id = this.state.contact_id;
        // Lookup name value
        let contactName = this.getNameForContact(this.state.contact_id);
        payload.params.AppBindContact.name = contactName;
      } else {
        payload.params.AppBindContact.name = this.state.contact_name;
      }
    } else {
      // Making a change to the current mapping
      payload.params.AppBindContact.map_id = appBindContact.accountDelegatedAuthsMapId;

      // New contact?
      if (this.state.contact_id === '') {
        payload.params.AppBindContact.name = this.state.contact_name;
      } else if (this.state.contact_id != null && appBindContact.entityId !== this.state.contact_id) {
        // Map to a different contact
        payload.params.AppBindContact.id = this.state.contact_id;
        // Lookup name value
        let contactName = this.getNameForContact(this.state.contact_id);
        payload.params.AppBindContact.name = contactName;
      } else {
        payload.params.AppBindContact = null; // Don't need to do anything
      }
    }

    // Asset account
    /*
    if (appBindAssetAccount == null) {
      // Existing or new asset account on new map.
      if (this.state.asset_id != null && this.state.asset_id != '') {
        // Mapping to an existing account
        payload.params.AppBindAssetAccount.id = this.state.asset_id;
        // Lookup code and name value
        let codeAndName = this.getCodeAndNameForAccount(this.state.asset_id);
        payload.params.AppBindAssetAccount.code = codeAndName.Code;
        payload.params.AppBindAssetAccount.name = codeAndName.Name;
      } else {
        // Set the code and name value
        payload.params.AppBindAssetAccount.code = this.state.asset_code;
        payload.params.AppBindAssetAccount.name = this.state.asset_name;
      }
    } else {
      // Making a change to the current mapping
      payload.params.AppBindAssetAccount.map_id = appBindAssetAccount.accountDelegatedAuthsMapId;

      // New asset account?
      if (this.state.asset_id === '') {
        payload.params.AppBindAssetAccount.code = this.state.asset_code;
        payload.params.AppBindAssetAccount.name = this.state.asset_name;
      } else if (this.state.asset_id != null && appBindAssetAccount.entityId !== this.state.asset_id) {
        // Map to a different account
        payload.params.AppBindAssetAccount.id = this.state.asset_id;
        // Lookup code and name value
        let codeAndName = this.getCodeAndNameForAccount(this.state.asset_id);
        payload.params.AppBindAssetAccount.code = codeAndName.Code;
        payload.params.AppBindAssetAccount.name = codeAndName.Name;
      } else {
        payload.params.AppBindAssetAccount = null; // Don't need to do anything
      }
    }
    */

    // Cogs account
    if (saasCogsAccount == null) {
      // Existing or new cogs account on new map.
      if (this.state.cogs_id != null && this.state.cogs_id != '') {
        // Mapping to an existing account
        payload.params.SaaSCOGSAccount.id = this.state.cogs_id;
        // Lookup code and name value
        let codeAndName = this.getCodeAndNameForAccount(this.state.cogs_id);
        payload.params.SaaSCOGSAccount.code = codeAndName.Code;
        payload.params.SaaSCOGSAccount.name = codeAndName.Name;
      } else {
        // Set the code and name value
        payload.params.SaaSCOGSAccount.code = this.state.cogs_code;
        payload.params.SaaSCOGSAccount.name = this.state.cogs_name;
      }
    } else {
      // Making a change to the current mapping
      payload.params.SaaSCOGSAccount.map_id = saasCogsAccount.accountDelegatedAuthsMapId;

      // New cogs account in Xero?
      if (this.state.cogs_id === '') {
        payload.params.SaaSCOGSAccount.code = this.state.cogs_code;
        payload.params.SaaSCOGSAccount.name = this.state.cogs_name;
      } else if (this.state.cogs_id != null && saasCogsAccount.entityId !== this.state.cogs_id) {
        // Map to a different account
        payload.params.SaaSCOGSAccount.id = this.state.cogs_id;
        // Lookup code and name value
        let codeAndName = this.getCodeAndNameForAccount(this.state.cogs_id);
        payload.params.SaaSCOGSAccount.code = codeAndName.Code;
        payload.params.SaaSCOGSAccount.name = codeAndName.Name;
      } else {
        payload.params.SaaSCOGSAccount = null; // Don't need to do anything
      }
    }

    // AppBind fee account
    if (abFeeAccount == null) {
      // Existing or new abfee account on new map.
      if (this.state.abfee_id != null && this.state.abfee_id != '') {
        // Mapping to an existing account
        payload.params.AppBindFeeAccount.id = this.state.abfee_id;
        // Lookup code and name value
        let codeAndName = this.getCodeAndNameForAccount(this.state.abfee_id);
        payload.params.AppBindFeeAccount.code = codeAndName.Code;
        payload.params.AppBindFeeAccount.name = codeAndName.Name;
      } else {
        // Set the code and name value
        payload.params.AppBindFeeAccount.code = this.state.abfee_code;
        payload.params.AppBindFeeAccount.name = this.state.abfee_name;
      }
    } else {
      // Making a change to the current mapping
      payload.params.AppBindFeeAccount.map_id = abFeeAccount.accountDelegatedAuthsMapId;

      // New abfee account?
      if (this.state.abfee_id === '') {
        payload.params.AppBindFeeAccount.code = this.state.abfee_code;
        payload.params.AppBindFeeAccount.name = this.state.abfee_name;
      } else if (this.state.abfee_id != null && abFeeAccount.entityId !== this.state.abfee_id) {
        // Map to a different account
        payload.params.AppBindFeeAccount.id = this.state.abfee_id;
        // Lookup code and name value
        let codeAndName = this.getCodeAndNameForAccount(this.state.abfee_id);
        payload.params.AppBindFeeAccount.code = codeAndName.Code;
        payload.params.AppBindFeeAccount.name = codeAndName.Name;
      } else {
        payload.params.AppBindFeeAccount = null; // Don't need to do anything
      }
    }

    // Payment processing fee account
    /*
    if (ppFeeAccount == null) {
      // Existing or new ppfee account on new map.
      if (this.state.ppfee_id != null && this.state.ppfee_id != '') {
        // Mapping to an existing account
        payload.params.AppBindPPAccount.id = this.state.ppfee_id;
        // Lookup code and name value
        let codeAndName = this.getCodeAndNameForAccount(this.state.ppfee_id);
        payload.params.AppBindPPAccount.code = codeAndName.Code;
        payload.params.AppBindPPAccount.name = codeAndName.Name;
      } else {
        // Set the code and name value
        payload.params.AppBindPPAccount.code = this.state.ppfee_code;
        payload.params.AppBindPPAccount.name = this.state.ppfee_name;
      }
    } else {
      // Making a change to the current mapping
      payload.params.AppBindPPAccount.map_id = ppFeeAccount.accountDelegatedAuthsMapId;

      // New ppfee account?
      if (this.state.ppfee_id === '') {
        payload.params.AppBindPPAccount.code = this.state.ppfee_code;
        payload.params.AppBindPPAccount.name = this.state.ppfee_name;
      } else if (this.state.ppfee_id != null && ppFeeAccount.entityId !== this.state.ppfee_id) {
        // Map to a different account
        payload.params.AppBindPPAccount.id = this.state.ppfee_id;
        // Lookup code and name value
        let codeAndName = this.getCodeAndNameForAccount(this.state.ppfee_id);
        payload.params.AppBindPPAccount.code = codeAndName.Code;
        payload.params.AppBindPPAccount.name = codeAndName.Name;
      } else {
        payload.params.AppBindPPAccount = null; // Don't need to do anything
      }
    }
    */

    // Bank account
    /*
    if (bankAccount == null) {
      // Existing bank account on new map.
      if (this.state.bank_id != null && this.state.bank_id != '') {
        // Mapping to an existing account
        payload.params.BankAccount.id = this.state.bank_id;
        // Lookup name value
        let codeAndName = this.getCodeAndNameForAccount(this.state.bank_id);
        payload.params.BankAccount.name = codeAndName.Name;
      }
    } else {
      // Making a change to the current mapping
      payload.params.BankAccount.map_id = bankAccount.accountDelegatedAuthsMapId;

      // Unset bank account?
      if (this.state.ppfee_id === '') {
        payload.params.BankAccount.id = null;
        payload.params.BankAccount.name = '';
      } else if (this.state.bank_id != null && bankAccount.entityId !== this.state.bank_id) {
        // Map to a different account
        payload.params.BankAccount.id = this.state.bank_id;
        // Lookup name value
        let codeAndName = this.getCodeAndNameForAccount(this.state.bank_id);
        payload.params.BankAccount.name = codeAndName.Name;
      } else {
        payload.params.BankAccount = null; // Don't need to do anything
      }
    }
    */

    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      if (data.link) {
        window.location.replace(data.link);
      } else {
        window.location.reload();
      }
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  render() {
    const { isAuthenticatedReseller, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticatedReseller() || needAssent()) { 
      return ( <Redirect to='/home' /> );
    }

    const params = new URLSearchParams(this.props.location.search);
    let adaId = params.get('adaId');

    let backText = "Settings";

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={IntegrationSetupXeroQuery}
        variables={{accountDelegatedAuthsId: adaId}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {
            let backLink = 'settings';
            if (props.accountDelegatedAuthsTByAccountDelegatedAuthsId === null)
            {
              return (
                <PageTitle title="Integration not found" showBackButton={true} name={backText} backLink={backLink} history={this.props.history}/>
              );
            }

            // If the state hasn't loaded yet, show spinner...
            if (this.state.acct_list == null || this.state.acct_list.length === 0) {
              if (this.state.lookup_error != null) {
                return (
                  <PageTitle title="Unable to load integration" showBackButton={true} name={backText} backLink={backLink} history={this.props.history}/>
                );
              }
              return (
                <LoadingSpinner />
              );
            }

            // Are there any mappings?
            let theIntegration = props.accountDelegatedAuthsTByAccountDelegatedAuthsId;
            let theLogo = theIntegration.appIntegrationTByAppIntegrationId.appLogo;
            let theDescription = theIntegration.appIntegrationTByAppIntegrationId.appDescription;

            //let theTitle = theIntegration.appName + ' integration';
            if (theIntegration.appData) {
                if (theIntegration.appName === 'Xero') {
                    let aTenant = theIntegration.appData.body[0];
                    let tenantName = aTenant.tenantName;
                    theDescription = theDescription + ' ' + tenantName;
                } else if (theIntegration.appName === 'QuickBooks') {
                    //let adJson = JSON.parse(theIntegration.appData);
                    let theRealm = theIntegration.appData.realmId;
                    theDescription = theDescription + ' Realm ' + theRealm;
                }
            }

            let theMaps = theIntegration.accountDelegatedAuthsMapTsByAccountDelegatedAuthsId;

            let appBindContact              = null;
            let appBindAssetAccount         = null;
            let saasCogsAccount             = null;
            let appBindFee                  = null;
            let appBindPaymentProcessingFee = null;
            let bankAccount                 = null;

            let mapsCount = theMaps.totalCount;

            if (mapsCount > 0) {
              theMaps.nodes.map(n => {
                if (n.mapName === "AppBindContact") {
                  appBindContact = n;
                } else if (n.mapName === "AppBindAssetAccount") {
                  appBindAssetAccount = n;
                } else if (n.mapName === "SaaSCOGSAccount") {
                  saasCogsAccount = n;
                } else if (n.mapName === "AppBindFeeAccount") {
                  appBindFee = n;
                } else if (n.mapName === "AppBindPPAccount") {
                  appBindPaymentProcessingFee = n;
                } else if (n.mapName === "BankAccount") {
                  bankAccount = n;
                }
              })
            }

            let theAppBindContactId = this.state.contact_id;
            if (theAppBindContactId == null) {
              if (appBindContact != null) {
                theAppBindContactId = appBindContact.entityId;
              } else {
                theAppBindContactId = '';
              }
            }

            let theAppBindAssetAccountId = this.state.asset_id;
            if (theAppBindAssetAccountId == null) {
              if (appBindAssetAccount != null) {
                theAppBindAssetAccountId = appBindAssetAccount.entityId;
              } else {
                theAppBindAssetAccountId = '';
              }
            }
            //let assetCodeError = (theAppBindAssetAccountId === '') && this.doesCodeAlreadyExist(this.state.asset_code);
            
            let theSaaSCogsAccountId = this.state.cogs_id;
            if (theSaaSCogsAccountId == null) {
              if (saasCogsAccount != null) {
                theSaaSCogsAccountId = saasCogsAccount.entityId;
              } else {
                theSaaSCogsAccountId = '';
              }
            }
            let cogsCodeError = (theSaaSCogsAccountId === '') && this.doesCodeAlreadyExist(this.state.cogs_code);

            let theAppBindFeeAccountId = this.state.abfee_id;
            if (theAppBindFeeAccountId == null) {
              if (appBindFee != null) {
                theAppBindFeeAccountId = appBindFee.entityId;
              } else {
                theAppBindFeeAccountId = '';
              }
            }
            let abfeeCodeError = (theAppBindFeeAccountId === '') && this.doesCodeAlreadyExist(this.state.abfee_code);

            let thePPFeeAccountId = this.state.ppfee_id;
            if (thePPFeeAccountId == null) {
              if (appBindPaymentProcessingFee != null) {
                thePPFeeAccountId = appBindPaymentProcessingFee.entityId;
              } else {
                thePPFeeAccountId = '';
              }
            }
            //let ppfeeCodeError = (thePPFeeAccountId === '') && this.doesCodeAlreadyExist(this.state.ppfee_code);

            let bankAccountId = this.state.bank_id;
            if (bankAccountId == null) {
              if (bankAccount != null) {
                bankAccountId = bankAccount.entityId;
              } else {
                bankAccountId = '';
              }
            }

            return (
              <Grid container spacing={3} direction="row" justifyContent="flex-start">
                <Grid item xs={12}>
                  <PageTitle title="Manage integration" showBackButton={true} name={backText} backLink={backLink} noMargin={true} history={this.props.history}/>
                </Grid>
                <Grid item xs={12}>
                  <Paper className={classes.paper}>
                    <Grid container spacing={2} direction="row" justifyContent="flex-start">
                      <Grid item>
                        <AppLogo logoPath={theLogo} appName={theIntegration.appName} imageHeight='48px'/>
                      </Grid>
                      <Grid item>
                        <Typography variant="h6">{theIntegration.appName}</Typography>
                        <Typography variant="body2" className={classes.description}>{theDescription}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                          AppBind contact
                        </Typography>
                        <Typography variant="body2" className={classes.description}>In order to create bills (supplier invoices) in Xero, there needs to be a contact in Xero for AppBind.  If one already exists, you can map to that existing contact, or you can create a new AppBind contact.</Typography>
                      </Grid>
                      <Grid item>
                        <FormControl className={classes.contactSelect}>
                          <InputLabel shrink htmlFor="contact">Contact mapping</InputLabel>
                          <NativeSelect
                            value={theAppBindContactId}
                            input={<Input name="contact" id="contact" />}
                            onChange={(e) => this.selectContactEntry(e.target.value)}
                          >
                            {this.state.contact_list.map(n => {
                              let theLabel = "Map to " + n.Name + ' (' + n.ContactID + ')';
                              if (theAppBindContactId === n.ContactID) {
                                theLabel = "Mapped to " + n.Name + ' (' + n.ContactID + ')';
                              }
                              return(<option key={n.ContactID} value={n.ContactID}>{theLabel}</option>);
                            })}
                            <option key="" value="">Create new contact for AppBind</option>
                          </NativeSelect>
                        </FormControl>
                      </Grid>
                      {theAppBindContactId === '' &&
                      <Grid item>
                        <TextField
                          className={classes.contactNameField}
                          id="contact_name" label="Name"
                          value={this.state.contact_name}
                          multiline
                          disabled={theAppBindContactId !== ''}
                          onChange={(e) => this.setState({ contact_name: e.target.value })} 
                          inputProps={{ maxLength: 150 }}
                        />
                      </Grid>}
                      {/* No asset account for now - simple expense only scenario 
                      <Grid item xs={12}>
                        <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                          AppBind asset account
                        </Typography>
                        <Typography variant="body2" className={classes.description}>An AppBind asset account represents an asset balance in AppBind.  This balance will be positive when markups and fees are collected from customers or vendors on your behalf.</Typography>
                      </Grid>
                      <Grid item>
                        <FormControl className={classes.accountSelect}>
                          <InputLabel shrink htmlFor="asset">Asset account</InputLabel>
                          <NativeSelect
                            value={theAppBindAssetAccountId}
                            input={<Input name="Asset" id="asset" />}
                            onChange={(e) => this.selectAssetEntry(e.target.value)}
                          >
                            {this.state.acct_list_cur.map(n => {
                              return(<option key={n.AccountID} value={n.AccountID}>{n.Code}: {n.Name}</option>);
                            })}
                            <option key="" value="">Create new asset account for AppBind</option>
                          </NativeSelect>
                        </FormControl>
                      </Grid>
                      {theAppBindAssetAccountId === '' &&
                      <Grid item>
                        <TextField
                          className={classes.codeField}
                          id="asset_code" label="Account code"
                          value={this.state.asset_code}
                          error={assetCodeError}
                          disabled={theAppBindAssetAccountId !== ''}
                          onChange={(e) => this.setState({ asset_code: e.target.value })} 
                          inputProps={{ maxLength: 10 }}
                        />
                      </Grid>}
                      {theAppBindAssetAccountId === '' &&
                      <Grid item>
                        <TextField
                          className={classes.descField}
                          id="asset_name" label="Name"
                          value={this.state.asset_name}
                          multiline
                          disabled={theAppBindAssetAccountId !== ''}
                          onChange={(e) => this.setState({ asset_name: e.target.value })} 
                          inputProps={{ maxLength: 150 }}
                        />
                      </Grid>}
                      */}

                      <Grid item xs={12}>
                        <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                          SaaS cost of goods/services sold
                        </Typography>
                        <Typography variant="body2" className={classes.description}>Bills (supplier invoices) for SaaS purchased for a customer should be associated with a cost of goods account.  You can map to an existing COGS account in Xero, or create a new one specifically for SaaS subscription COGS.</Typography>
                      </Grid>
                      <Grid item>
                        <FormControl className={classes.accountSelect}>
                          <InputLabel shrink htmlFor="cogs">Cost of good/services sold</InputLabel>
                          <NativeSelect
                            value={theSaaSCogsAccountId}
                            input={<Input name="cogs" id="cogs" />}
                            onChange={(e) => this.selectCogsEntry(e.target.value)}
                          >
                            {this.state.acct_list_dir.map(n => {
                              return(<option key={n.AccountID} value={n.AccountID}>{n.Code}: {n.Name}</option>);
                            })}
                            <option key="" value="">Create a new COGS account</option>
                          </NativeSelect>
                        </FormControl>
                      </Grid>
                      {theSaaSCogsAccountId === '' &&
                      <Grid item>
                        <TextField
                          className={classes.codeField}
                          id="cogs_code" label="Account code"
                          value={this.state.cogs_code}
                          error={cogsCodeError}
                          disabled={theSaaSCogsAccountId !== ''}
                          onChange={(e) => this.setState({ cogs_code: e.target.value })} 
                          inputProps={{ maxLength: 10 }}
                        />
                      </Grid>}
                      {theSaaSCogsAccountId === '' &&
                      <Grid item>
                        <TextField
                          className={classes.descField}
                          id="cogs_name" label="Name"
                          value={this.state.cogs_name}
                          multiline
                          disabled={theSaaSCogsAccountId !== ''}
                          onChange={(e) => this.setState({ cogs_name: e.target.value })} 
                          inputProps={{ maxLength: 150 }}
                        />
                      </Grid>}

                      <Grid item xs={12}>
                        <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                          SaaS expense
                        </Typography>
                        <Typography variant="body2" className={classes.description}>Bills (supplier invoices) for SaaS purchased for your own use should be associated with an expense account.  You can map to an existing expense account in Xero, or create a new one specifically for SaaS subscription expenses.</Typography>
                      </Grid>
                      <Grid item>
                        <FormControl className={classes.accountSelect}>
                          <InputLabel shrink htmlFor="abfee">SaaS expense</InputLabel>
                          <NativeSelect
                            value={theAppBindFeeAccountId}
                            input={<Input name="abfee" id="abfee" />}
                            onChange={(e) => this.selectAbfeeEntry(e.target.value)}
                          >
                            {this.state.acct_list_exp.map(n => {
                              return(<option key={n.AccountID} value={n.AccountID}>{n.Code}: {n.Name}</option>);
                            })}
                            <option key="" value="">Create a new expense account</option>
                          </NativeSelect>
                        </FormControl>
                      </Grid>
                      {theAppBindFeeAccountId === '' &&
                      <Grid item>
                        <TextField
                          className={classes.codeField}
                          id="abfee_code" label="Account code"
                          value={this.state.abfee_code}
                          error={abfeeCodeError}
                          disabled={theAppBindFeeAccountId !== ''}
                          onChange={(e) => this.setState({ abfee_code: e.target.value })} 
                          inputProps={{ maxLength: 10 }}
                        />
                      </Grid>}
                      {theAppBindFeeAccountId === '' &&
                      <Grid item>
                        <TextField
                          className={classes.descField}
                          id="abfee_name" label="Name"
                          value={this.state.abfee_name}
                          multiline
                          disabled={theAppBindFeeAccountId !== ''}
                          onChange={(e) => this.setState({ abfee_name: e.target.value })} 
                          inputProps={{ maxLength: 150 }}
                        />
                      </Grid>}

                      {/* For now, don't split out payment processing fees
                      <Grid item xs={12}>
                        <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                          Payment processing fees
                        </Typography>
                        <Typography variant="body2" className={classes.description}>Invoice line items for payment processing fees (assessed for credit card purchases) are associated with this account.</Typography>
                      </Grid>
                      <Grid item>
                        <FormControl className={classes.accountSelect}>
                          <InputLabel shrink htmlFor="paymentfee">Payment processing fees</InputLabel>
                          <NativeSelect
                            value={thePPFeeAccountId}
                            input={<Input name="paymentfee" id="paymentfee" />}
                            onChange={(e) => this.selectPpfeeEntry(e.target.value)}
                          >
                            {this.state.acct_list_exp.map(n => {
                              return(<option key={n.AccountID} value={n.AccountID}>{n.Code}: {n.Name}</option>);
                            })}
                            <option key="" value="">Create a new account</option>
                          </NativeSelect>
                        </FormControl>
                      </Grid>
                      { thePPFeeAccountId === '' &&
                      <Grid item>
                        <TextField
                          className={classes.codeField}
                          id="ppfee_code" label="Account code"
                          value={this.state.ppfee_code}
                          error={ppfeeCodeError}
                          disabled={thePPFeeAccountId !== ''}
                          onChange={(e) => this.setState({ ppfee_code: e.target.value })} 
                          inputProps={{ maxLength: 10 }}
                        />
                      </Grid>}
                      { thePPFeeAccountId === '' &&
                      <Grid item>
                        <TextField
                          className={classes.descField}
                          id="ppfee_name" label="Name"
                          value={this.state.ppfee_name}
                          multiline
                          disabled={thePPFeeAccountId !== ''}
                          onChange={(e) => this.setState({ ppfee_name: e.target.value })} 
                          inputProps={{ maxLength: 150 }}
                        />
                      </Grid>}
                      */}

                      {/* In simple scenario, there are no band / credit card account mappings
                      <Grid item xs={12}>
                        <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                          Bank / credit card account
                        </Typography>
                        <Typography variant="body2" className={classes.description}>Optionally, you can assign a bank account in Xero for transactions that add value to your asset account in AppBind.  This should be the account in Xero that corresponds to the default funding source you have on file with AppBind.</Typography>
                      </Grid>
                      <Grid item>
                        <FormControl className={classes.accountSelect}>
                          <InputLabel shrink htmlFor="bank">Bank / CC account</InputLabel>
                          <NativeSelect
                            value={bankAccountId}
                            input={<Input name="bank" id="bank" />}
                            onChange={(e) => this.selectBankEntry(e.target.value)}
                          >
                            {this.state.acct_list_bank.map(n => {
                              return(<option key={n.AccountID} value={n.AccountID}>{n.Name}</option>);
                            })}
                            <option key="" value="">Don't map a bank / credit card account</option>
                          </NativeSelect>
                        </FormControl>
                      </Grid>
                      */}

                      <Grid item xs={12}/>

                      <Grid item>
                        <Button color="primary" variant="contained" disabled={this.isSaveDisabled(theAppBindContactId, theAppBindAssetAccountId, theSaaSCogsAccountId, theAppBindFeeAccountId, thePPFeeAccountId)} 
                                onClick={()=> {
                                  this.saveSettings(adaId, appBindContact, appBindAssetAccount, saasCogsAccount, appBindFee, appBindPaymentProcessingFee, bankAccount)
                                }}>
                          Save settings
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button color="primary" variant="outlined" onClick={()=> {this.disconnectFromApp(adaId)}}>Disconnect from Xero</Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            );
          }
          return (
            <LoadingSpinner />
          );
        }}
      />
    );
  }
}
  
IntegrationSetupXero.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(IntegrationSetupXero);
